"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { useAuth } from "@/components/AuthProvider";
import { getDocumentStats, getDocuments, API_BASE, getAuthHeaders, getTokenUsageSummary, getConfigLLM } from "@/lib/api";

// Types
interface Stats {
  total: number;
  by_status: Record<string, number>;
}

interface Document {
  id: string;
  file_hash: string;
  original_filename: string;
  renamed_filename: string | null;
  document_type: string | null;
  status: string;
  confidence: number | null;
  source: string;
  drive_url: string | null;
  folder_url: string | null;
  created_at: string | null;
  processed_at: string | null;
  errors: string[] | null;
  extracted_data?: Record<string, any> | null;
}

const STATUS_CONFIG: Record<string, { label: string; class: string }> = {
  completado: { label: "Completado", class: "badge-success" },
  procesando: { label: "Procesando", class: "badge-processing" },
  pendiente: { label: "Pendiente", class: "badge-warning" },
  en_revision: { label: "Revisión", class: "badge-info" },
  error: { label: "Error", class: "badge-error" },
  duplicado: { label: "Duplicado", class: "badge-neutral" },
};

const TYPE_LABELS: Record<string, string> = {
  factura_a: "Factura A",
  factura_b: "Factura B",
  factura_c: "Factura C",
  factura_otro: "Factura Otro",
  recibo_pago: "Recibo de Pago",
  transferencia: "Transferencia",
  vep: "VEP",
  resumen_tarjeta: "Resumen Tarjeta",
  servicio_luz: "Luz",
  servicio_agua: "Agua",
  servicio_gas: "Gas",
  abl: "ABL",
  expensas: "Expensas",
  sueldo: "Sueldo",
  fci: "FCI",
  impuesto_afip: "Impuesto AFIP",
  otro: "Otro",
};

const PROVIDER_LABELS: Record<string, string> = {
  gemini: "Google Gemini", openai: "OpenAI", claude: "Anthropic Claude",
  deepseek: "DeepSeek", moonshot: "Moonshot",
};

// --- Sorting helpers ---
type SortField = "filename" | "type" | "status" | "confidence" | "source" | "fecha_doc" | "fecha_proc";
type SortDir = "asc" | "desc";

function getDocDate(doc: Document): string {
  return doc.extracted_data?.fecha_emision || doc.extracted_data?.fecha_vencimiento || doc.extracted_data?.fecha || "";
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [health, setHealth] = useState<string>("checking");
  const [tokenUsage, setTokenUsage] = useState<any>(null);
  const [tokenPeriod, setTokenPeriod] = useState<number>(7);
  const [sortField, setSortField] = useState<SortField>("fecha_proc");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  // Configuración de modelos IA (leída de /api/config/llm)
  const [aiConfig, setAiConfig] = useState<{ primary: string; verifier: string; fallback: string } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string | number> = { limit: 20 };
      if (statusFilter) params.status = statusFilter;

      const [statsRes, docsRes] = await Promise.allSettled([
        getDocumentStats(),
        getDocuments(params as any),
      ]);

      if (statsRes.status === "fulfilled") setStats(statsRes.value);
      if (docsRes.status === "fulfilled") {
        const docs = docsRes.value.documents || [];
        setDocuments(docs);
      }

      // --- Obtener config LLM para mostrar modelos seleccionados ---
      if (!aiConfig) {
        try {
          const cfg = await getConfigLLM();
          const prov = cfg.provider || "desconocido";
          const model = cfg.model || "?";
          const fallbackProv = cfg.fallback_provider || "";
          const fallbackModel = cfg.fallback_model || "";
          const verifEnabled = cfg.verification_enabled ?? false;
          const verifProv = cfg.verification_provider || "";
          const verifModel = cfg.verification_model || "";
          setAiConfig({
            primary: `${PROVIDER_LABELS[prov] || prov} (${model})`,
            verifier: verifEnabled
              ? `${PROVIDER_LABELS[verifProv] || verifProv || "?"} ${verifModel ? `(${verifModel})` : ""}`
              : "Deshabilitado",
            fallback: fallbackProv
              ? `${PROVIDER_LABELS[fallbackProv] || fallbackProv} ${fallbackModel ? `(${fallbackModel})` : ""}`
              : "No configurado",
          });
        } catch {
          // Config no disponible, se mantiene null
        }
      }

      // Health (endpoint público, sin auth)
      try {
        const cacheBuster = `?_t=${Date.now()}`;
        const h = await fetch(`${API_BASE}/api/health${cacheBuster}`).then((r) => r.json());
        setHealth(h.status === "healthy" ? "online" : "degraded");
      } catch {
        setHealth("offline");
      }
    } catch (e: any) {
      setError(e.message || "Error al cargar datos");
      setStats({ total: 0, by_status: {} });
      setDocuments([]);
      setHealth("offline");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Cargar consumo de tokens
  useEffect(() => {
    getTokenUsageSummary(tokenPeriod)
      .then(setTokenUsage)
      .catch(() => setTokenUsage(null));
  }, [tokenPeriod]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // --- Sorting ---
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedDocuments = useMemo(() => {
    const sorted = [...documents];
    sorted.sort((a, b) => {
      let valA: any, valB: any;
      switch (sortField) {
        case "filename":
          valA = (a.renamed_filename || a.original_filename).toLowerCase();
          valB = (b.renamed_filename || b.original_filename).toLowerCase();
          break;
        case "type":
          valA = a.document_type || "";
          valB = b.document_type || "";
          break;
        case "status":
          valA = a.status;
          valB = b.status;
          break;
        case "confidence":
          valA = a.confidence ?? -1;
          valB = b.confidence ?? -1;
          break;
        case "source":
          valA = a.source;
          valB = b.source;
          break;
        case "fecha_doc":
          valA = getDocDate(a) || "zzzz";
          valB = getDocDate(b) || "zzzz";
          break;
        case "fecha_proc":
          valA = a.processed_at || a.created_at || "";
          valB = b.processed_at || b.created_at || "";
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [documents, sortField, sortDir]);

  const SortIcon = ({ field }: { field: SortField }) => (
    <span style={{ marginLeft: "4px", opacity: sortField === field ? 1 : 0.3, fontSize: "10px" }}>
      {sortField === field ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
    </span>
  );

  const completados = stats?.by_status?.completado || 0;
  const procesando = stats?.by_status?.procesando || 0;
  const pendientes = stats?.by_status?.pendiente || 0;
  const errores = stats?.by_status?.error || 0;

  return (
    <div className="app-layout">
      <Sidebar />
      <Header
        title="Dashboard"
        subtitle="Vista general del sistema"
        actions={
          <div className="flex items-center gap-3">
            <span
              className={`badge ${health === "online"
                ? "badge-success"
                : health === "degraded"
                  ? "badge-warning"
                  : "badge-error"
                }`}
            >
              {health === "online" ? "Sistema online" : health === "degraded" ? "Degradado" : "Offline"}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={fetchData} id="refresh-btn" title="Actualizar todos los datos del dashboard">
              🔄 Actualizar
            </button>
            {user && (
              <span style={{ fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>
                👤 {user.display_name}
              </span>
            )}
            <button
              className="btn btn-ghost btn-sm"
              onClick={logout}
              title="Cerrar sesión"
              style={{ fontSize: "12px" }}
            >
              🚪
            </button>
          </div>
        }
      />

      <main className="main-content">
        {/* Stats */}
        <div className="stat-grid stagger-children">
          <div className="stat-card">
            <div className="stat-icon indigo">📄</div>
            <div className="stat-content">
              <div className="stat-label">Total documentos</div>
              <div className="stat-value">{stats?.total ?? "—"}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">✓</div>
            <div className="stat-content">
              <div className="stat-label">Completados</div>
              <div className="stat-value" style={{ color: "var(--status-success)" }}>
                {completados}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon yellow">⏳</div>
            <div className="stat-content">
              <div className="stat-label">Pendientes</div>
              <div className="stat-value" style={{ color: "var(--status-warning)" }}>
                {pendientes}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon purple">⚡</div>
            <div className="stat-content">
              <div className="stat-label">Procesando</div>
              <div className="stat-value" style={{ color: "var(--status-processing)" }}>
                {procesando}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon red">✖</div>
            <div className="stat-content">
              <div className="stat-label">Errores</div>
              <div className="stat-value" style={{ color: "var(--status-error)" }}>
                {errores}
              </div>
            </div>
          </div>
        </div>

        {/* Modelos IA configurados */}
        {aiConfig && (
          <div style={{
            display: "flex", gap: "var(--space-4)", flexWrap: "wrap",
            margin: "var(--space-3) 0", padding: "10px var(--space-4)",
            background: "var(--bg-secondary)", borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-subtle)", fontSize: "12px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>🤖 Principal:</span>
              <span style={{ color: "var(--accent-primary)", fontWeight: 600, padding: "2px 8px", background: "var(--accent-primary-alpha)", borderRadius: "var(--radius-sm)" }}>
                {aiConfig.primary}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>🔍 Verificador:</span>
              <span style={{ color: "#ffc107", fontWeight: 600, padding: "2px 8px", background: "rgba(255,193,7,0.12)", borderRadius: "var(--radius-sm)" }}>
                {aiConfig.verifier}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>🔄 Fallback:</span>
              <span style={{ color: "var(--text-tertiary)", fontWeight: 600, padding: "2px 8px", background: "var(--bg-tertiary)", borderRadius: "var(--radius-sm)" }}>
                {aiConfig.fallback}
              </span>
            </div>
          </div>
        )}

        {/* Token Usage Section */}
        <div className="card animate-fade-in" style={{ marginTop: "var(--space-4)" }}>
          <div className="card-header">
            <div>
              <h2 className="card-title">💰 Consumo de Tokens e IA</h2>
              <p className="card-subtitle">Uso de tokens y costos estimados por proveedor</p>
            </div>
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              {[7, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setTokenPeriod(d)}
                  className={`btn btn-sm ${tokenPeriod === d ? "btn-primary" : "btn-ghost"}`}
                  style={{ fontSize: "12px" }}
                >
                  {d === 7 ? "7 días" : "30 días"}
                </button>
              ))}
            </div>
          </div>
          <div style={{ padding: "var(--space-4)" }}>
            {tokenUsage ? (
              <>
                {/* Totales */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--space-4)", marginBottom: "var(--space-4)" }}>
                  <div style={{ padding: "16px", borderRadius: "var(--radius-md)", background: "var(--bg-secondary)", textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--accent-primary)" }}>
                      {(tokenUsage.totals?.total_tokens || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>Tokens totales</div>
                  </div>
                  <div style={{ padding: "16px", borderRadius: "var(--radius-md)", background: "var(--bg-secondary)", textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--success)" }}>
                      ${(tokenUsage.totals?.cost_usd || 0).toFixed(4)}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>Costo USD</div>
                  </div>
                  <div style={{ padding: "16px", borderRadius: "var(--radius-md)", background: "var(--bg-secondary)", textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" }}>
                      {tokenUsage.totals?.num_operations || 0}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>Operaciones</div>
                  </div>
                </div>

                {/* Desglose por proveedor */}
                {tokenUsage.by_provider && Object.keys(tokenUsage.by_provider).length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                    {Object.entries(tokenUsage.by_provider).map(([provider, data]: [string, any]) => (
                      <div key={provider} style={{
                        padding: "12px 16px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--border-subtle)",
                        background: "var(--bg-primary)",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 600, fontSize: "14px" }}>
                            {provider === "gemini" ? "🧠" : provider === "openai" ? "🟢" : provider === "claude" ? "🔶" : "🔵"} {provider.charAt(0).toUpperCase() + provider.slice(1)}
                          </span>
                          <span style={{ fontSize: "13px", color: "var(--success)", fontWeight: 600 }}>
                            ${data.total_cost_usd?.toFixed(4)}
                          </span>
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>
                          {data.total_input_tokens?.toLocaleString()} input + {data.total_output_tokens?.toLocaleString()} output — {data.num_operations} ops
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: "13px", color: "var(--text-tertiary)", textAlign: "center", padding: "20px" }}>
                    Sin datos de consumo en los últimos {tokenPeriod} días
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: "13px", color: "var(--text-tertiary)", textAlign: "center", padding: "20px" }}>
                Cargando datos de consumo...
              </p>
            )}
          </div>
        </div>

        {/* Recent Documents — tabla sorteable */}
        <div className="card animate-fade-in">
          <div className="card-header">
            <div>
              <h2 className="card-title">Documentos recientes</h2>
              <p className="card-subtitle">Últimos 20 documentos procesados</p>
            </div>
            <div className="flex gap-2">
              <select
                className="input select"
                style={{ width: "auto", minWidth: "140px" }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                id="status-filter"
              >
                <option value="">Todos</option>
                <option value="completado">Completados</option>
                <option value="procesando">Procesando</option>
                <option value="pendiente">Pendientes</option>
                <option value="error">Errores</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="skeleton" style={{ height: "48px", width: "100%" }} />
              ))}
            </div>
          ) : documents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <div className="empty-state-title">Sin documentos</div>
              <p className="empty-state-text">
                {health === "offline"
                  ? "No se pudo conectar al backend. Verificá que esté corriendo en el puerto 8000."
                  : "Subí tu primer documento para empezar a clasificar."}
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table" id="documents-table">
                <thead>
                  <tr>
                    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("filename")} title="Ordenar por nombre de archivo">
                      Archivo<SortIcon field="filename" />
                    </th>
                    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("type")} title="Ordenar por tipo de documento">
                      Tipo<SortIcon field="type" />
                    </th>
                    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("status")} title="Ordenar por estado de procesamiento">
                      Estado<SortIcon field="status" />
                    </th>
                    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("confidence")} title="Ordenar por porcentaje de confianza de la clasificación">
                      Confianza<SortIcon field="confidence" />
                    </th>
                    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("source")} title="Ordenar por fuente de ingreso (manual, telegram, email)">
                      Fuente<SortIcon field="source" />
                    </th>
                    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("fecha_doc")} title="Ordenar por fecha del comprobante (emision/vencimiento)">
                      Fecha Doc<SortIcon field="fecha_doc" />
                    </th>
                    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("fecha_proc")} title="Ordenar por fecha en que el sistema procesó el documento">
                      Procesado<SortIcon field="fecha_proc" />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.map((doc) => {
                    const statusCfg = STATUS_CONFIG[doc.status] || STATUS_CONFIG.error;
                    const docDate = getDocDate(doc);
                    return (
                      <tr key={doc.id}>
                        <td>
                          <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                            {doc.renamed_filename || doc.original_filename}
                          </div>
                          {doc.renamed_filename && (
                            <div style={{ fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>
                              {doc.original_filename}
                            </div>
                          )}
                        </td>
                        <td>
                          {doc.document_type ? (
                            <span className="badge badge-info">
                              {TYPE_LABELS[doc.document_type] || doc.document_type}
                            </span>
                          ) : (
                            <span className="text-muted text-xs">—</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${statusCfg.class}`}>{statusCfg.label}</span>
                        </td>
                        <td>
                          {doc.confidence !== null ? (
                            <span
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: "var(--text-sm)",
                                color:
                                  doc.confidence >= 0.8
                                    ? "var(--status-success)"
                                    : doc.confidence >= 0.6
                                      ? "var(--status-warning)"
                                      : "var(--status-error)",
                              }}
                            >
                              {(doc.confidence * 100).toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-muted text-xs">—</span>
                          )}
                        </td>
                        <td>
                          <span className="text-xs" style={{ textTransform: "capitalize" }}>
                            {doc.source}
                          </span>
                        </td>
                        <td>
                          <span className="text-xs text-muted" title={docDate ? `Fecha del comprobante: ${docDate}` : "Sin fecha en el comprobante"}>
                            {docDate || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="text-xs text-muted" title={doc.processed_at ? `Procesado el: ${new Date(doc.processed_at).toLocaleString("es-AR")}` : doc.created_at ? `Subido el: ${new Date(doc.created_at).toLocaleString("es-AR")}` : ""}>
                            {doc.processed_at
                              ? new Date(doc.processed_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                              : doc.created_at
                                ? new Date(doc.created_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                                : "—"
                            }
                          </span>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            {doc.drive_url && (
                              <a
                                href={doc.drive_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-ghost btn-sm"
                                title="Ver el archivo archivado en Google Drive"
                              >
                                🖼️
                              </a>
                            )}
                            {doc.folder_url && (
                              <a
                                href={doc.folder_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-ghost btn-sm"
                                title="Abrir la carpeta de categoría asignada en Google Drive"
                              >
                                📁
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
